/**
 * Advanced Works ERP — Utilitaires spécifiques au contexte algérien.
 *
 * Formatage monétaire et des dates au format DZ, et calculs paie/fiscaux
 * (IEP, IRG) selon les paramètres légaux algériens. Les barèmes et taux
 * sont paramétrables côté admin ; les valeurs par défaut figurent ici.
 */

/* --------------------------------------------------------------------------
 * Formatage monétaire (DZD) — séparateur de milliers « . », décimale « , ».
 * Ex : formatDZD(1250000) -> « 1.250.000,00 DZD »
 * ------------------------------------------------------------------------ */
export function formatNumberDZ(value: number | string, decimals = 2): string {
  // L'API sérialise les Decimals en chaînes (« 5000000.00 ») : on convertit.
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) return decimals > 0 ? '0,00' : '0';
  const fixed = Math.abs(num).toFixed(decimals);
  const [intPart, decPart] = fixed.split('.');
  const withThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const sign = num < 0 ? '-' : '';
  return decimals > 0 ? `${sign}${withThousands},${decPart}` : `${sign}${withThousands}`;
}

export function formatDZD(
  amount: number | string,
  options?: { decimals?: number; symbol?: string },
): string {
  const decimals = options?.decimals ?? 2;
  const symbol = options?.symbol ?? 'DZD';
  return `${formatNumberDZ(amount, decimals)} ${symbol}`;
}

/**
 * Montant DZD pour les **cartes KPI** : compact, lisible et SANS troncature.
 * - < 1 million      -> chiffre complet (ex. « 850.000 DZD »)
 * - >= 1 million      -> « 103,0 M DZD »
 * - >= 1 milliard     -> « 1,25 Md DZD »
 * Les tableaux, totaux et détails utilisent formatDZD (chiffre complet, jamais abrégé).
 */
export function formatDZDCompact(amount: number | string): string {
  const num = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(num)) return '—';
  const abs = Math.abs(num);
  if (abs >= 1_000_000_000) return `${formatNumberDZ(num / 1_000_000_000, 2)} Md DZD`;
  if (abs >= 1_000_000) return `${formatNumberDZ(num / 1_000_000, 1)} M DZD`;
  return formatDZD(num, { decimals: 0 });
}

/* --------------------------------------------------------------------------
 * Dates — format DZ « JJ/MM/AAAA ».
 * ------------------------------------------------------------------------ */
function toDate(date: Date | string | number): Date | null {
  const d = date instanceof Date ? date : new Date(date);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDateDZ(date: Date | string | number): string {
  const d = toDate(date);
  if (!d) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${d.getFullYear()}`;
}

export function formatDateTimeDZ(date: Date | string | number): string {
  const d = toDate(date);
  if (!d) return '—';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${formatDateDZ(d)} ${hh}:${mm}`;
}

/** Nombre de jours entre aujourd'hui et `date` (négatif si la date est passée). */
export function daysUntil(date: Date | string | number): number {
  const d = toDate(date);
  if (!d) return Number.NaN;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86_400_000);
}

/* --------------------------------------------------------------------------
 * Paie / fiscalité algérienne.
 * ------------------------------------------------------------------------ */

/**
 * IEP — Indemnité d'Expérience Professionnelle.
 * 1 % du salaire de base par année d'ancienneté, plafonné à 15 %.
 */
export function calculateIEP(
  salaireBase: number,
  anneesAnciennete: number,
  options?: { tauxParAn?: number; plafondPct?: number },
): number {
  const tauxParAn = options?.tauxParAn ?? 0.01;
  const plafondPct = options?.plafondPct ?? 0.15;
  const pct = Math.min(Math.max(anneesAnciennete, 0) * tauxParAn, plafondPct);
  return Math.round(salaireBase * pct * 100) / 100;
}

/** Une tranche du barème IRG. `max = null` => tranche supérieure ouverte. */
export interface TrancheIRG {
  min: number;
  max: number | null;
  taux: number; // ex. 0.20 pour 20 %
}

/**
 * Barème IRG salaires par défaut (mensuel, DZD). Paramétrable en admin.
 * Exonération jusqu'à 20 000 DZD. Source : barème progressif DGI.
 */
export const BAREME_IRG_DEFAULT: TrancheIRG[] = [
  { min: 0, max: 20_000, taux: 0 },
  { min: 20_000, max: 40_000, taux: 0.23 },
  { min: 40_000, max: 80_000, taux: 0.27 },
  { min: 80_000, max: 160_000, taux: 0.3 },
  { min: 160_000, max: 320_000, taux: 0.33 },
  { min: 320_000, max: null, taux: 0.35 },
];

/**
 * IRG progressif : applique chaque tranche sur la portion du revenu qui la
 * concerne. Retourne le montant d'impôt arrondi au dinar.
 */
export function calculateIRG(
  brutImposable: number,
  bareme: TrancheIRG[] = BAREME_IRG_DEFAULT,
): number {
  if (brutImposable <= 0) return 0;
  let irg = 0;
  for (const tranche of bareme) {
    if (brutImposable <= tranche.min) break;
    const plafond = tranche.max ?? brutImposable;
    const portion = Math.min(brutImposable, plafond) - tranche.min;
    if (portion > 0) irg += portion * tranche.taux;
  }
  return Math.round(irg);
}

/* --------------------------------------------------------------------------
 * Taux légaux par défaut (paramétrables en admin — table parametres_fiscaux).
 * ------------------------------------------------------------------------ */
export const PARAMETRES_FISCAUX_DEFAUT = {
  tvaStandard: 0.19,
  tvaReduite: 0.09,
  tap: 0.02,
  cnasSalarie: 0.09,
  cnasPatronal: 0.26,
  iepTauxParAn: 0.01,
  iepPlafondPct: 0.15,
  irgPlafondExoneration: 20_000,
  seuilPaiementEspecesB2B: 50_000,
} as const;

/** TVA + TTC à partir d'un HT. */
export function computeTVA(montantHT: number, tauxTVA = PARAMETRES_FISCAUX_DEFAUT.tvaStandard) {
  const tvaMontant = Math.round(montantHT * tauxTVA * 100) / 100;
  return { tvaMontant, montantTTC: Math.round((montantHT + tvaMontant) * 100) / 100 };
}
